import React from "react";
import { Flex, Grid, ModalFooter, Text } from "@chakra-ui/react";
import { DateTime } from "luxon";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { PathPickCreatedOrderResponse } from "@/features/pathpick/types/response";

interface Props {
  isOpen: boolean;
  // 계속 상품 보기 / 헤더 X — 주문 흐름 전체를 닫는다.
  onClose: () => void;
  // 주문 생성 POST 응답. 입금 정보와 주문 id가 모두 여기 담겨 와 추가 조회가 필요 없다.
  order: PathPickCreatedOrderResponse;
}

const MILLIS_IN_HOUR = 60 * 60 * 1000;

// 시안이 24시간제("2026. 08. 06. 22:26")라 오전/오후를 쓰는 formatPathPickOrderDateTime은 쓰지 않는다.
const formatDueAt = (iso: string) =>
  DateTime.fromISO(iso).setLocale("ko").toFormat("yyyy. MM. dd. HH:mm");

// 기한까지 남은 시간(올림). 서버가 기한을 정하므로 정책상의 36시간을 상수로 박지 않는다.
const getRemainingHours = (iso: string) => {
  const remaining = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(remaining) || remaining <= 0) return 0;
  return Math.ceil(remaining / MILLIS_IN_HOUR);
};

interface InfoFieldProps {
  label: string;
  value?: string | null;
}

// 주문 상세의 입금 정보 필드와 같은 표기. 하위 feature를 끌어오지 않으려고 같은 모양으로 둔다.
function InfoField({ label, value }: InfoFieldProps) {
  return (
    <Flex flexDirection={"column"} gap={"2px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {value || "-"}
      </Text>
    </Flex>
  );
}

// 주문 요청 완료 안내 모달 — 선입금 후 수량 확정 흐름이라 접수 직후 입금 금액·기한·계좌를 알려준다.
// 담은 상품과 상품 찾기 양쪽의 주문 수량 모달이 공용으로 띄운다.
function PathPickOrderCompleteModal({ isOpen, onClose, order }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const { navigate } = useRouterHelper();

  const remittance = order.remittance;
  // 상세 화면과 같은 폴백 규칙 — 확정 전 주문은 remittance.amount가 비어 올 수 있다.
  const amount = remittance?.amount ?? order.totalOrderedAmount ?? 0;
  const dueAt = remittance?.dueAt ?? null;
  const remainingHours = dueAt ? getRemainingHours(dueAt) : 0;
  // 은행/계좌 중 하나라도 없으면 반쪽짜리 문자열 대신 "-"로 떨어뜨린다.
  const account =
    remittance?.bankName && remittance?.accountNumber
      ? `${remittance.bankName} ${remittance.accountNumber}`
      : null;

  const handleGoToDetail = () => {
    navigate(`/pathpick/order-history/${order.id}`);
    onClose();
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(560px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:basket.order_complete.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <Flex flexDirection={"column"} gap={"4px"}>
            <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
              {t("path_pick:basket.order_complete.heading")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.order_complete.body")}
            </Text>
          </Flex>

          <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={"16px"}
            padding={"16px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            background={"#F7F9FA"}
          >
            <Flex flexDirection={"column"} gap={"2px"}>
              <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                {t("path_pick:basket.order_complete.amount")}
              </Text>
              <Text textStyle={"body-lg-bold"} color={"#00417D"}>
                {tCurrencyString(amount, {
                  type: "SYMBOL",
                  code: order.currency,
                })}
              </Text>
            </Flex>
            <InfoField
              label={t("path_pick:basket.order_complete.due")}
              value={
                dueAt
                  ? t("path_pick:basket.order_complete.due_value", {
                      dateTime: formatDueAt(dueAt),
                      hours: remainingHours,
                    })
                  : null
              }
            />
            <InfoField
              label={t("path_pick:basket.order_complete.account")}
              value={account}
            />
            <InfoField
              label={t("path_pick:basket.order_complete.account_holder")}
              value={remittance?.accountHolder}
            />
            <InfoField
              label={t("path_pick:basket.order_complete.remitter")}
              value={remittance?.remitterName}
            />
          </Grid>

          <Flex
            gap={"8px"}
            padding={"12px 16px"}
            borderRadius={"8px"}
            background={"#FFFBEB"}
          >
            <CustomIcon
              src={"warning-triangle"}
              width={16}
              height={16}
              color={"#B27700"}
            />
            <Flex flexDirection={"column"} gap={"4px"}>
              <Text textStyle={"caption-medium"} color={"#B27700"}>
                {t("path_pick:basket.order_complete.notice_deadline", {
                  hours: remainingHours,
                })}
              </Text>
              <Text textStyle={"caption-medium"} color={"#B27700"}>
                {t("path_pick:basket.order_complete.notice_cancel")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>

      {/* 공용 Footer는 버튼 폭이 96px 고정이라 긴 라벨이 들어가지 않아 직접 구성한다. */}
      <ModalFooter padding={"12px 16px"} gap={"8px"}>
        <AppButton variant={"white"} onClick={handleGoToDetail}>
          <Text textStyle={"body-sm-bold"}>
            {t("path_pick:basket.order_complete.go_to_detail")}
          </Text>
        </AppButton>
        <AppButton variant={"primary"} onClick={onClose}>
          <Text textStyle={"body-sm-bold"}>
            {t("path_pick:basket.order_complete.continue")}
          </Text>
        </AppButton>
      </ModalFooter>
    </CustomApprovalModal>
  );
}

export default PathPickOrderCompleteModal;
